import Phaser from 'phaser';
import type { TilesetId } from '../config/tilesets.js';
import { TILESET_ROOTS, DEFAULT_TILESET } from '../config/tilesets.js';

/**
 * Shared Phaser tile-terrain renderer — the canvas equivalent of the plaza's
 * CSS tile recipe (`LobbyScene.renderPlaza`/`renderBlocks`): a **grass-capped
 * solid-earth block** drawn from the Sunnyland terrain atlas. Where the plaza
 * draws it with DOM `background:url(...)`, the stages are a canvas Phaser scene,
 * so this draws the SAME look with `TileSprite`s.
 *
 * CP-4: the block is now a proper NINE-SLICE so platforms read naturally —
 * grass-cap TOP (aligned to the collision surface, the CP-3 invariant), dirt
 * BODY, a wavy dirt BOTTOM edge (not a hard flat cut), plus dirt-edge LEFT/RIGHT
 * sides — instead of grass-over-flat-dirt repeated to a hard bottom cut.
 *
 * One module so the plaza look is defined once and every real stage reuses it
 * (StageScene static terrain + runtime-spawned bridge terrain). Cosmetic only —
 * collision/sim are untouched.
 */

/** Source tile png is 128×128; the plaza renders it 1:1 (128px display tiles). */
const SRC_TILE = 128;
/**
 * On-screen tile display size (px) for stages. Smaller than the plaza's 128 so
 * the texture detail reads at the tighter stage scale (terrain rects are often
 * only 1–2 tiles tall), while keeping the same grass-cap-over-dirt recipe.
 */
const DISPLAY_TILE = 64;
/** Grass-cap band height (px) drawn at the block top — the grass-top atlas row. */
const GRASS_CAP = DISPLAY_TILE;
/**
 * Wavy dirt BOTTOM-edge band height (px). The atlas bottom-edge tiles carry the
 * organic wave in roughly their lower ~28% (≈36px of 128), so this band height
 * shows the full wave without clipping it.
 */
const BOTTOM_EDGE = 36;
/**
 * Dirt-edge SIDE band width (px). The left/right atlas variants carry the dirt
 * side texture in their outer column; this is how wide we crop it on screen.
 */
const SIDE_EDGE = 18;
/**
 * Extra px the dirt body is filled BELOW a block's logical bottom for blocks that
 * are NOT given a wavy bottom edge (e.g. the world-floor slab), so no seam / stray
 * grass edge peeks under them (the plaza's "fill past the floor's grass band").
 */
const FILL_OVERHANG = 24;

/**
 * Sunnyland terrain atlas (`tiles/N.png`), grass-capped 3×3 nine-slice:
 *   row 0 — grass cap:   1 left · 2 center · 3 right
 *   row 1 — dirt body:   4 left · 5 center · 6 right
 *   row 2 — dirt bottom: 12 left · 9 center · 16 right  (wavy bottom edge)
 * The icezone region ships the SAME filenames, so the map is shared.
 */
const ATLAS = {
  capL: '1.png',
  capC: '2.png',
  capR: '3.png',
  bodyL: '4.png',
  bodyC: '5.png',
  bodyR: '6.png',
  botL: '12.png',
  botC: '9.png',
  botR: '16.png',
} as const;
type AtlasSlot = keyof typeof ATLAS;
const ATLAS_SLOTS = Object.keys(ATLAS) as AtlasSlot[];

/** Phaser texture key for a tileset's atlas slot (per region). */
const tileKey = (id: TilesetId, slot: AtlasSlot): string => `tt-${id}-${slot}`;

/**
 * Queue every terrain atlas tile for a tileset region. Call from a scene's
 * `preload()` (idempotent — Phaser skips an already-loaded key). Defaults to
 * `'sunnyland'`.
 */
export function preloadTileset(scene: Phaser.Scene, tileset: TilesetId = DEFAULT_TILESET): void {
  const root = TILESET_ROOTS[tileset];
  for (const slot of ATLAS_SLOTS) {
    const key = tileKey(tileset, slot);
    if (!scene.textures.exists(key)) {
      scene.load.image(key, `${root}/tiles/${ATLAS[slot]}`);
    }
  }
}

const TILE_SCALE = DISPLAY_TILE / SRC_TILE;

// -- DOM (plaza) recipe atoms ---------------------------------------------------
// The plaza paints the SAME terrain with CSS `background:url(...) /128px` layers:
// a grass-cap row over a warm dirt fill. These exports are the single source of
// the tile art so the DOM plaza and the canvas stages can never drift.

/** Plaza (DOM) display tile size — the plaza renders the 128px source 1:1. */
export const DOM_TILE = SRC_TILE;

/** URL of the grass-cap tile (the plaza's ground/cap `repeat-x` row). */
export function grassCapUrl(tileset: TilesetId = DEFAULT_TILESET): string {
  return `${TILESET_ROOTS[tileset]}/tiles/${ATLAS.capC}`;
}

/**
 * URL of the warm textured dirt tile the plaza fills block bodies with
 * (`10.png` — matches the cap's dirt band; the canvas nine-slice uses the
 * atlas body tiles instead).
 */
export function dirtFillUrl(tileset: TilesetId = DEFAULT_TILESET): string {
  return `${TILESET_ROOTS[tileset]}/tiles/10.png`;
}

/** Add one origin-(0,0) tileSprite of an atlas slot at the given screen rect. */
function strip(
  scene: Phaser.Scene,
  tileset: TilesetId,
  slot: AtlasSlot,
  x: number,
  y: number,
  w: number,
  h: number,
): Phaser.GameObjects.TileSprite {
  return scene.add
    .tileSprite(x, y, w, h, tileKey(tileset, slot))
    .setOrigin(0, 0)
    .setTileScale(TILE_SCALE, TILE_SCALE);
}

/**
 * Draw a deep DIRT underfill band (no grass cap) spanning a wide area below the
 * ground plane, so the zoomed-out stage view shows continuous earth under the
 * terrain instead of a sky-coloured gap below the thin platform strips.
 * Decorative only — drawn behind the real grass-capped collision terrain; it adds
 * no collision (mirrors the plaza, whose mountain blocks fill dirt to the floor).
 */
export function drawDirtFill(
  scene: Phaser.Scene,
  layer: Phaser.GameObjects.Container,
  band: { left: number; top: number; w: number; h: number },
  tileset: TilesetId = DEFAULT_TILESET,
): Phaser.GameObjects.TileSprite {
  const dirt = strip(scene, tileset, 'bodyC', band.left, band.top, band.w, band.h);
  layer.add(dirt);
  return dirt;
}

/** A solid-earth block to draw, in screen px (y down): top-left + size. */
export interface TiledBlock {
  left: number;
  top: number;
  w: number;
  h: number;
}

export interface TiledBlockOptions {
  /**
   * Draw the wavy dirt BOTTOM edge + dirt-edge sides for a natural exposed block
   * (the default — every raised platform / ground slab). Set `false` for blocks
   * whose bottom should melt into terrain below (uses the seam-hiding overhang
   * instead) — e.g. a tall world-floor slab whose bottom is off-screen.
   */
  edges?: boolean;
}

/**
 * Draw one grass-capped solid-earth block (plaza look) into `layer` as a proper
 * nine-slice from the Sunnyland atlas: grass-cap TOP (aligned to the block top =
 * the collision surface, CP-3 invariant), dirt BODY, wavy dirt BOTTOM edge, and
 * dirt-edge LEFT/RIGHT sides. Returns the created game objects (added to `layer`).
 *
 * `layer` is a Container so the caller controls depth/order; the block is drawn
 * relative to the layer's origin (pass screen-space coords if the layer is at 0,0).
 */
export function drawTiledBlock(
  scene: Phaser.Scene,
  layer: Phaser.GameObjects.Container,
  block: TiledBlock,
  tileset: TilesetId = DEFAULT_TILESET,
  opts: TiledBlockOptions = {},
): Phaser.GameObjects.GameObject[] {
  const { left, top, w } = block;
  const edges = opts.edges ?? true;
  const out: Phaser.GameObjects.GameObject[] = [];

  // Horizontal band heights. The grass cap always sits at the top; the bottom edge
  // (when drawn) sits at the block's logical bottom; dirt body fills between. Bands
  // are clamped so they never overlap on short blocks (cap wins, then bottom).
  const capH = Math.min(GRASS_CAP, block.h);
  const botH = edges ? Math.min(BOTTOM_EDGE, block.h - capH) : 0;
  // Body height: when no wavy bottom, fill PAST the base (overhang) to hide seams.
  const bodyTop = top + capH;
  const bodyH = edges
    ? Math.max(0, block.h - capH - botH)
    : Math.max(0, block.h - capH) + FILL_OVERHANG;
  const botTop = top + block.h - botH;

  // Side-edge column widths (only on real exposed blocks wide enough for them).
  const side = edges ? Math.min(SIDE_EDGE, Math.floor(w / 2)) : 0;
  const midX = left + side;
  const midW = w - side * 2;

  // -- center column (always) -------------------------------------------------
  out.push(strip(scene, tileset, 'capC', midX, top, midW, capH));
  if (bodyH > 0) out.push(strip(scene, tileset, 'bodyC', midX, bodyTop, midW, bodyH));
  if (botH > 0) out.push(strip(scene, tileset, 'botC', midX, botTop, midW, botH));

  // -- left / right edge columns (exposed blocks only) ------------------------
  if (side > 0) {
    out.push(strip(scene, tileset, 'capL', left, top, side, capH));
    if (bodyH > 0) out.push(strip(scene, tileset, 'bodyL', left, bodyTop, side, bodyH));
    if (botH > 0) out.push(strip(scene, tileset, 'botL', left, botTop, side, botH));

    const rx = left + w - side;
    out.push(strip(scene, tileset, 'capR', rx, top, side, capH));
    if (bodyH > 0) out.push(strip(scene, tileset, 'bodyR', rx, bodyTop, side, bodyH));
    if (botH > 0) out.push(strip(scene, tileset, 'botR', rx, botTop, side, botH));
  }

  layer.add(out);
  return out;
}
